import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

/**
 * Thin animated progress bar pinned to the top of the viewport.
 * Renders whenever any TanStack Query is fetching OR any mutation
 * is in flight — covers list-loads, edits, lookups, validates, the
 * import-with-vendor-enrich path, etc., without each call site
 * having to opt in.
 *
 * The bar is purely indeterminate (a bright stripe sliding across a
 * dim track). For long requests we don't know the percentage anyway,
 * and the motion is what tells the user the page hasn't frozen — a
 * static fill would be no better than the existing "Saving…" labels.
 *
 * A short delay before showing dampens flicker: a 100ms request
 * shouldn't draw a bar that flashes for 100ms then vanishes. Only
 * requests that take >150ms light up the bar.
 */
const SHOW_DELAY_MS = 150;
const HIDE_DELAY_MS = 200;


export function GlobalProgressBar() {
  const fetching = useIsFetching();
  const mutating = useIsMutating();
  const active = fetching > 0 || mutating > 0;

  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    if (active) {
      // Schedule a show — if the request finishes inside SHOW_DELAY_MS
      // the timer fires after `active` has flipped back to false and
      // the cleanup below clears it before anything renders.
      timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    } else {
      // Slight hide delay so a follow-up request that fires within
      // 200ms (e.g. list invalidate after a save) doesn't blink the
      // bar off and back on.
      timer = setTimeout(() => setVisible(false), HIDE_DELAY_MS);
    }
    return () => {
      if (timer !== null) clearTimeout(timer);
    };
  }, [active]);

  if (!visible) return null;
  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-0 z-50 h-0.5 overflow-hidden bg-forge-100 dark:bg-forge-900"
      role="progressbar"
      aria-busy="true"
      aria-label="Loading"
    >
      <div className="h-full w-1/4 bg-forge-600 dark:bg-forge-400 animate-progress-slide" />
    </div>
  );
}
